/**
 * `useLeads` — live read of every CRM lead the signed-in user can
 * see in their primary org.
 *
 * Mirrors the mobile `src/features/crm/useLeads.ts` hook. The
 * Firestore rule (`firestore.rules` → `match /leads/{leadId}`) gates
 * reads to any org member, so the web SDK can `onSnapshot` directly
 * with no Cloud Function in front.
 *
 * Sort: `createdAt` desc (newest first), client-side. Legacy docs
 * without a `createdAt` fall to the end.
 */
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { useCallback, useEffect, useState } from 'react';

import { db } from '@/lib/firebase';
import { useFullOrganization } from '@/lib/useFullOrganization';

// LeadStatus + LeadPriority moved to `@shared/perms/lead` so
// mobile and web share one definition. Re-exported here so
// existing imports from this module keep working.
import type { LeadStatus, LeadPriority } from '@shared/perms';
export type { LeadStatus, LeadPriority };

// LeadSource / ProjectType / PossessionStatus / BudgetRange all
// moved to `@shared/perms/lead` so mobile + web share one copy.
// Re-exported here so existing imports from this module keep
// working unchanged.
import type {
  LeadSource,
  ProjectType,
  PossessionStatus,
  BudgetRange,
} from '@shared/perms';
export type {
  LeadSource,
  ProjectType,
  PossessionStatus,
  BudgetRange,
};

export type LeadListItem = {
  id: string;
  orgId: string;
  name: string;
  phone: string;
  email?: string;
  source: LeadSource;
  status: LeadStatus;
  priority: LeadPriority;
  /** Legacy free-number field. Prefer `budgetRange` going forward. */
  budget?: number;
  /** NEW — bucketed range string from the form. */
  budgetRange?: BudgetRange;
  /** NEW — what stage the property is at. */
  possessionStatus?: PossessionStatus;
  /** NEW (web) — already in mobile schema. */
  projectType?: ProjectType;
  location?: string;
  notes?: string;
  followUpAt?: { toMillis(): number } | null;
  expectedStartDate?: { toMillis(): number } | null;
  createdAt?: { toMillis(): number } | null;
  assignedTo?: string;
  /** REMOVED from the form per the redesign — kept in the type only
   *  so legacy docs still read without a type error. New writes
   *  shouldn't set this field. */
  tags?: string[];
};

export type UseLeadsResult = {
  leads: LeadListItem[];
  loading: boolean;
  /** True while the initial subscription is establishing. */
  initialLoading: boolean;
  error: string | null;
  orgId: string | null;
  /** True when the loaded window is full — there MAY be older leads
   *  the user hasn't pulled in yet. False = we've reached the end. */
  hasMore: boolean;
  /** Bump the live window by `PAGE_SIZE` more docs. */
  loadMore: () => void;
  /** Total docs currently loaded. */
  loaded: number;
};

/** Default page size — covers ~95% of studios in a single
 *  subscription. Studios beyond this paginate via `loadMore()`. */
const PAGE_SIZE = 200;

export function useLeads(): UseLeadsResult {
  const { org, loading: orgLoading } = useFullOrganization();
  const [leads, setLeads] = useState<LeadListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Window size — starts at PAGE_SIZE, bumps by PAGE_SIZE on every
  // `loadMore()`. The Firestore subscription re-establishes with the
  // new limit; everything stays live-realtime within the loaded window.
  const [windowSize, setWindowSize] = useState(PAGE_SIZE);
  const [hasMore, setHasMore] = useState(false);

  const orgId = org?.id ?? null;

  useEffect(() => {
    if (orgLoading) return;
    if (!orgId) {
      setLeads([]);
      setLoading(false);
      setInitialLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    return onSnapshot(
      query(
        collection(db, 'leads'),
        where('orgId', '==', orgId),
        orderBy('createdAt', 'desc'),
        limit(windowSize),
      ),
      (snap) => {
        const rows = snap.docs.map((d) => ({
          ...(d.data() as LeadListItem),
          id: d.id,
        }));
        setLeads(rows);
        // If we filled the window exactly, there's MAYBE more — show
        // the "Load more" button. If we got fewer than the limit, we
        // know we're at the end.
        setHasMore(rows.length >= windowSize);
        setLoading(false);
        setInitialLoading(false);
      },
      (err) => {
        console.warn('[useLeads] snapshot error', err);
        setError(err.message);
        setLoading(false);
        setInitialLoading(false);
      },
    );
  }, [orgId, orgLoading, windowSize]);

  // Reset window when org changes so we start fresh.
  useEffect(() => {
    setWindowSize(PAGE_SIZE);
  }, [orgId]);

  const loadMore = useCallback(() => {
    setWindowSize((n) => n + PAGE_SIZE);
  }, []);

  return {
    leads,
    loading,
    initialLoading,
    error,
    orgId,
    hasMore,
    loadMore,
    loaded: leads.length,
  };
}

// ───── Display helpers (shared by the page) ─────

export function leadStatusLabel(s: LeadStatus): string {
  switch (s) {
    case 'new':                   return 'New';
    case 'contacted':             return 'Contacted';
    case 'site_visit_scheduled':  return 'Site visit';
    case 'proposal_sent':         return 'Proposal sent';
    case 'negotiation':           return 'Negotiation';
    case 'converted':             return 'Converted';
    case 'lost':                  return 'Lost';
  }
}

export function leadPriorityLabel(p: LeadPriority): string {
  return p[0].toUpperCase() + p.slice(1);
}

export function leadSourceLabel(s: LeadSource): string {
  switch (s) {
    case 'walk_in':     return 'Walk-in';
    case 'reference':   return 'Client Referral';
    case 'instagram':   return 'Instagram';
    case 'website':     return 'Website / SEO';
    case 'just_dial':   return 'JustDial';
    case 'google_ads':  return 'Google Ads';
    case 'other':       return 'Other';
  }
}

export function possessionStatusLabel(p: PossessionStatus): string {
  switch (p) {
    case 'under_construction': return 'Under Construction';
    case 'ready_to_move':      return 'Ready to Move';
    case 'old_property':       return 'Old Property (Renovation)';
  }
}

export function projectTypeLabel(t: ProjectType): string {
  switch (t) {
    case '1bhk':       return '1BHK Interior';
    case '2bhk':       return '2BHK Interior';
    case '3bhk':       return '3BHK Interior';
    case 'villa':      return '4BHK+ / Villa';
    case 'commercial': return 'Commercial / Office';
    case 'office':     return 'Office';
    case 'renovation': return 'Renovation';
    case 'other':      return 'Other';
  }
}

export function budgetRangeLabel(r: BudgetRange): string {
  switch (r) {
    case 'under_10l':  return 'Under ₹10L';
    case '10_15l':     return '₹10L – ₹15L';
    case '15_25l':     return '₹15L – ₹25L';
    case '25_40l':     return '₹25L – ₹40L';
    case 'above_40l':  return 'Above ₹40L';
  }
}

/** Form-facing option lists.
 *
 *  Every array below is COMPUTED from the canonical `{ key, label }[]`
 *  enum in `@shared/perms` via `toFormOptions()`. Hides + label
 *  overrides are declarative + explicit so the form's UX choices
 *  stay readable without re-listing the entire enum (= no chance
 *  of a typo'd enum value sneaking into the form).
 *
 *  Adding a new enum value in shared auto-appears in the form
 *  unless explicitly hidden here. */
import {
  LEAD_SOURCES,
  LEAD_PRIORITIES,
  LEAD_STATUSES,
  POSSESSION_STATUSES,
  PROJECT_TYPES,
  BUDGET_RANGES,
} from '@shared/perms';
import { toFormOptions } from '@/lib/formOptions';

export const SOURCE_FORM_OPTIONS = toFormOptions(LEAD_SOURCES, {
  // `just_dial` + `google_ads` stay in the type union for legacy
  // doc reads but the new-write form shrinks the picker to 5.
  hide: ['just_dial', 'google_ads'],
  labels: {
    website: 'Website / SEO',
    reference: 'Client Referral',
  },
});

export const PRIORITY_FORM_OPTIONS = toFormOptions(LEAD_PRIORITIES);

export const STATUS_FORM_OPTIONS = toFormOptions(LEAD_STATUSES, {
  labels: {
    new: 'New Request',
    site_visit_scheduled: 'Site Visit Done',
    proposal_sent: 'Proposal Sent',
  },
});

export const POSSESSION_FORM_OPTIONS = toFormOptions(POSSESSION_STATUSES);

export const PROJECT_TYPE_FORM_OPTIONS = toFormOptions(PROJECT_TYPES, {
  // 'office' is a legacy value; 'other' is captured separately
  // in the form via a free-text field, not the picker.
  hide: ['office', 'other'],
});

export const BUDGET_RANGE_FORM_OPTIONS = toFormOptions(BUDGET_RANGES);

/** "active" = anything in flight (not converted / not lost).
 *  Re-exported from `@shared/perms` (`LEAD_PIPELINE_ACTIVE`) under
 *  the existing local name so callers in this codebase don't have
 *  to change their imports. */
import { LEAD_PIPELINE_ACTIVE } from '@shared/perms';
export const ACTIVE_LEAD_STATUSES: LeadStatus[] = LEAD_PIPELINE_ACTIVE;

export function isActiveLead(s: LeadStatus): boolean {
  return ACTIVE_LEAD_STATUSES.includes(s);
}

// ───── Single-doc hook for the detail page ─────

export type UseLeadResult = {
  lead: LeadListItem | null;
  loading: boolean;
  error: string | null;
};

export function useLead(leadId: string | undefined): UseLeadResult {
  const [lead, setLead] = useState<LeadListItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!leadId) {
      setLead(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    return onSnapshot(
      doc(db, 'leads', leadId),
      (snap) => {
        if (!snap.exists()) setLead(null);
        else setLead({ ...(snap.data() as LeadListItem), id: snap.id });
        setLoading(false);
      },
      (err) => {
        console.warn('[useLead] snapshot error', err);
        setError(err.message);
        setLoading(false);
      },
    );
  }, [leadId]);

  return { lead, loading, error };
}
